import { Link } from "react-router-dom";
import { Shield, Mail, Phone } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import ContourBackground from "@/components/ContourBackground";
import Reveal from "@/components/Reveal";

const LAST_UPDATED = "March 1, 2026";

export default function Privacy() {
  useSeo({
    title: "Privacy Policy — Tri-Axis XYZ CAD Solutions",
    description:
      "How Tri-Axis XYZ CAD Solutions, LLC collects, uses and protects the information firms share with us through our website, quote forms and project workflows.",
  });

  return (
    <div data-testid="page-privacy">
      <section className="relative overflow-hidden border-b border-slate-200 bg-paper-dark/40">
        <ContourBackground variant="light" opacity={0.5} seed={13} />
        <div className="relative mx-auto max-w-4xl px-6 lg:px-10 py-14 lg:py-20">
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo flex items-center gap-2">
            <Shield className="h-3 w-3" /> Legal · Privacy
          </div>
          <h1 className="display mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900">
            Privacy Policy
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
            We are a B2B CAD drafting and land-survey CAD services provider. Your
            trust matters — this page explains, in plain English, what we collect,
            why, and how we look after it.
          </p>
          <p className="mt-3 mono text-[11px] tracking-wide text-slate-500">
            Last updated: {LAST_UPDATED}
          </p>
        </div>
      </section>

      <section className="bg-white border-b border-slate-200 py-14">
        <div className="mx-auto max-w-3xl px-6 lg:px-10 space-y-10">
          <Reveal>
            <Section title="1. Who we are">
              <p>
                <strong>Tri-Axis XYZ CAD Solutions, LLC</strong> ("Tri-Axis", "we",
                "our", or "us") is a CAD drafting service provider incorporated
                in the United States. Our registered office is in Orlando,
                Florida. You can contact us at{" "}
                <a className="text-emerald-700 hover:underline" href="mailto:contact@triaxisxyz.com">
                  contact@triaxisxyz.com
                </a>{" "}
                or <strong>+91 80836 91588</strong>.
              </p>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="2. Information we collect">
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <strong>Contact / quote form data</strong> — name, email, company,
                  phone, country, state, the service you're interested in, and the
                  message or project details you submit.
                </li>
                <li>
                  <strong>Project files you share with us</strong> — CAD drawings,
                  point clouds, field notes, PDFs and other technical files
                  delivered via email, secure file transfer, or our forms.
                </li>
                <li>
                  <strong>Usage data</strong> — your browser type, approximate
                  geolocation, referring URL and pages visited, gathered through
                  standard server logs and basic analytics.
                </li>
                <li>
                  <strong>Cookies / local storage</strong> — small technical
                  identifiers used to keep the site working (for example,
                  remembering that you've already unlocked a checklist download).
                </li>
              </ul>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="3. How we use it">
              <ul className="list-disc pl-5 space-y-2">
                <li>To respond to your quote, brief or general inquiry.</li>
                <li>
                  To produce the drafting deliverables you engage us for, and to
                  share progress, drafts, RFIs and final outputs back with you.
                </li>
                <li>
                  To improve our website, services, turnaround times and QC
                  process.
                </li>
                <li>
                  To send transactional emails (quote confirmations, project
                  status, invoices). We do not send marketing emails unless you
                  have opted in.
                </li>
                <li>
                  To comply with legal, accounting, tax and audit obligations.
                </li>
              </ul>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="4. Confidentiality of your project files">
              <p>
                Drawings, surveys, point clouds and any other technical material
                you provide are treated as <strong>confidential</strong>. They are
                used solely to deliver the engagement you have asked us to do.
                We do not republish, resell or use them as portfolio collateral
                without your explicit written permission. If your firm requires
                a signed NDA before sharing files, simply reply to our first
                email — we sign yours, or send ours.
              </p>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="5. Sharing with third parties">
              <p>We share information only with vendors that help us run our business, and only to the extent strictly necessary:</p>
              <ul className="list-disc pl-5 mt-2 space-y-2">
                <li>
                  <strong>Cloud / hosting</strong> — the infrastructure that
                  serves this website and stores form submissions in a managed
                  database.
                </li>
                <li>
                  <strong>File transfer</strong> — encrypted file-sharing
                  services used to receive and deliver large CAD packages.
                </li>
                <li>
                  <strong>Payment / accounting</strong> — invoicing platforms,
                  to bill you and meet tax obligations.
                </li>
                <li>
                  <strong>Maps / fonts</strong> — public map embeds (e.g. Google
                  Maps) and web-font CDNs that may set their own cookies under
                  their respective privacy policies.
                </li>
              </ul>
              <p className="mt-3">
                We <strong>do not sell</strong> personal data, project files or
                contact details to third parties.
              </p>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="6. How long we keep it">
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Active project files: for the duration of the engagement plus
                  a reasonable archive period (typically 12–24 months) so you
                  can ask for re-issues.
                </li>
                <li>
                  Quote / contact form submissions: kept while we evaluate the
                  inquiry and for a follow-up window of up to 24 months, unless
                  you ask us to delete sooner.
                </li>
                <li>
                  Accounting records: retained for the period required by
                  applicable tax law (typically 7 years in the US).
                </li>
              </ul>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="7. Your rights">
              <p>
                You can ask us at any time to (a) confirm what information we
                hold about you, (b) correct anything that's wrong, (c) delete
                data we no longer need to keep, or (d) port your data out in a
                standard format. Email{" "}
                <a className="text-emerald-700 hover:underline" href="mailto:contact@triaxisxyz.com">
                  contact@triaxisxyz.com
                </a>{" "}
                with "Privacy request" in the subject line and we will respond
                within 30 days.
              </p>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="8. Security">
              <p>
                We use encrypted transport (HTTPS), access-controlled databases
                and least-privilege authentication tokens for our admin tools.
                Despite our best efforts no method of transmission is 100%
                secure; if you believe an incident has occurred, please contact
                us immediately.
              </p>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="9. International transfers">
              <p>
                Tri-Axis serves clients across the USA, Canada and Australia.
                Information may be processed in any country where we, our
                clients, or our service providers operate. By engaging us you
                consent to such transfers, subject to safeguards required by
                applicable law.
              </p>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="10. Changes to this policy">
              <p>
                We may update this Privacy Policy as our services evolve. The
                "Last updated" date at the top of this page will always reflect
                the current version. Material changes will be highlighted in our
                next email correspondence with you.
              </p>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="11. Contact us">
              <ul className="space-y-2">
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-emerald-700" />
                  <a className="hover:underline" href="mailto:contact@triaxisxyz.com">
                    contact@triaxisxyz.com
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-emerald-700" />
                  <a className="hover:underline" href="tel:+918083691588">
                    +91 80836 91588
                  </a>
                </li>
              </ul>
              <p className="mt-4 text-sm text-slate-500">
                Also see our{" "}
                <Link to="/terms" data-testid="privacy-to-terms" className="text-emerald-700 hover:underline">
                  Terms &amp; Conditions
                </Link>
                .
              </p>
            </Section>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="display text-xl md:text-2xl font-extrabold tracking-tight text-slate-900">
        {title}
      </h2>
      <div className="mt-3 text-slate-700 leading-relaxed text-[15px]">{children}</div>
    </div>
  );
}
